import { cn } from '@/lib/cn.js';

/**
 * Single-line editorial title lockup for the conference name.
 *
 * Reads as one flowing sentence:
 *   5ᵗʰ World Conference on  Information Systems  for Business Management
 *
 * Colour cues come exclusively from the site's existing palette:
 *   • `text-gradient-brand` for the edition numeral (same treatment as StatsBand)
 *   • `text-brand-600 / dark:text-brand-300` for the ordinal superscript
 *   • `font-display italic text-brand-600 / dark:text-brand-300` to highlight
 *     the subject phrase — the established emphasis pattern used across the
 *     site (see PreviousProceedings, ImportantDates, PublicationsStrip).
 */
export function ConferenceTitleLockup({ title, className }) {
  const { editionNumber, editionOrdinal, overline, headline, subline } = title;
  const subject = headline.join(' ');

  return (
    <h2
      className={cn(
        'h-display text-balance text-ink-900 dark:text-ink-50',
        'text-[2rem] sm:text-[2.5rem] lg:text-[3rem] leading-[1.08] tracking-[-0.025em]',
        className,
      )}
    >
      <span className="inline-flex items-start tabular-nums leading-none align-top mr-3 sm:mr-4">
        <span className="text-gradient-brand">{editionNumber}</span>
        <sup className="text-[0.45em] font-semibold leading-none mt-[0.12em] -ml-[0.04em] text-brand-600 dark:text-brand-300">
          {editionOrdinal}
        </sup>
      </span>
      <span className="text-ink-700 dark:text-ink-200">{overline}</span>{' '}
      <em className="font-display italic text-brand-600 dark:text-brand-300">
        {subject}
      </em>{' '}
      <span className="text-ink-700 dark:text-ink-200">{subline}</span>
    </h2>
  );
}
